import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Agent } from '../agent'
import type * as common from '../common'

import type { Access } from './access'
import type { User } from './user'
import type { Zone } from './zone'

// NOTE: this makes creating a TypeChecker easier
type TenantBase = common.Identified<Tenant.Id> &
  common.Coded &
  common.Named &
  common.Aliased &
  common.Colored &
  common.CreatedBy<User.Id>
/**
 * Represents a customer instance of Attain Apps. Typically, this is a
 * university or other higher education institution
 */
export interface Tenant extends TenantBase {
  /**
   * Credits shared by this tenant's users per month. Undefined means
   * unlimited.
   */
  readonly monthlyCreditLimit?: number
}
export namespace Tenant {
  /**
   * Identifies a {@link Tenant}
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> =
      TypeChecker.isString.usingAlias('Tenant.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Information needed to create a {@link Tenant}
   *
   * Excludes `monthlyCreditLimit`: it is set through `core.agent.usage`, which
   * restricts it to Platform Admins, rather than through tenant editing.
   */
  export type Input = Omit<Tenant, 'createdBy' | 'id' | 'monthlyCreditLimit'>

  /**
   * Information used to update a {@link Tenant}
   */
  export type Updates = Partial<Input>

  /**
   * {@link Tenant} with additional information pertaining to the associated
   * {@link User}
   */
  export interface ForUser extends Tenant, Access.Holder {
    /**
     * {@link Zone.ForUser} for the zones that this user has access to in
     * this {@link Tenant.ForUser}
     */
    zones: readonly Zone.ForUser[]
    /**
     * {@link Agent.ForUser} for the agents this user has access to at the
     * tenant level
     */
    agents: readonly Agent.ForUser[]
  }
}
